import React, { useState } from 'react';
import {
  List,
  ListItem,
  Button,
  Typography,
  Stack,
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Popover,
  Tooltip,
} from '../../../ui/core';
import { useFormatters, useCopyToClipboard, useToast } from '../../../hooks';
import { useDispatch } from 'react-redux';
import { cancelRide as apiCancelRide, reinstateTripRide, pickUpRide, dropOffRide } from '../../../redux/Trips/actions';
import { RideStatus } from '../../../models/Rides';
import { useHasPermission } from '../../../helpers/api/auth/index';

import UserCard from './UserCard';
import MakeItRightDialog from './MakeItRightDialog';
import EditRideLocationDialog from './EditRideLocationDialog';
import TransferRideDialog from './TransferRideDialog';
import {
  CheckCircleIcon,
  CloseIcon,
  CancelIcon,
  MoreVertIcon,
  ArrowForwardIcon,
  CircleIcon,
  ContentCopyIcon,
  WarningIcon,
} from '../../../ui/icons';
import UserCrispLink from '../Users/UserCrispLink';
import moment from 'moment';

export const RideStatusChip = ({ status }) => {
  switch (status) {
    case RideStatus.processing:
      return <Chip icon={<CircleIcon sx={{ fontSize: 16 }} />} color="warning" label="Processing" />;
    case RideStatus.finalizing_details:
      return <Chip icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} color="warning" label="Finalizing Details" />;
    case RideStatus.accepted:
      return <Chip icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} color="info" label="Accepted" />;
    case RideStatus.en_route:
      return <Chip icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} color="info" label="Picked-up" />;
    case RideStatus.completed:
      return <Chip icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} color="info" label="Completed" />;
    case RideStatus.cancelled:
      return <Chip icon={<CancelIcon sx={{ fontSize: 16 }} />} color="cancelled" label="Cancelled" />;
    default:
      return null;
  }
};

const DepartureTimes = ({ ride, trip }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const f = useFormatters();

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button aria-describedby="departure_time_details" onClick={handleOpen} sx={{ paddingLeft: '0px' }}>
        <Stack direction="row" spacing="8px">
          <Typography color="text.secondary" variant="body2">
            Departure time
          </Typography>
          <Typography>
            {f.date.toTimezoneLocaleString(
              ride.departureTime,
              trip.originCity.timeZone,
              f.date.DateFormats.TIME_SIMPLE,
            )}
          </Typography>
        </Stack>
      </Button>
      <Popover
        id="departure_time_details"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p="24px" width="375px">
          <Typography variant="h5" mb="16px">
            Departure Time Details
          </Typography>
          {ride.departureTimeHistory && ride.departureTimeHistory.length > 0 && (
            <>
              <Typography variant="body2" fontWeight={700} mb="8px">
                Departure Time History
              </Typography>
              {ride.departureTimeHistory.map((history, index) => (
                <Box key={index} mb="8px">
                  <Typography variant="body2">
                    Past Departure Time:{' '}
                    {f.date.toTimezoneLocaleString(
                      history.pastDeptTime,
                      trip.originCity.timeZone,
                      f.date.DateFormats.DATETIME_FULL,
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Changed at:{' '}
                    {f.date.toTimezoneLocaleString(
                      history.timestamp,
                      trip.originCity.timeZone,
                      f.date.DateFormats.DATETIME_FULL,
                    )}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Popover>
    </div>
  );
};

export const RideFare = ({ ride, label = 'Trip price' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const f = useFormatters();

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button aria-describedby="price_breakdown" onClick={handleOpen}>
        <Stack direction="row" spacing="8px">
          <Typography color="text.secondary" variant="body2">
            {label}
          </Typography>
          <Typography>{f.number.currency(ride.fare.finalAmount)}</Typography>
        </Stack>
      </Button>
      <Popover
        id="price_breakdown"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p="24px" width="375px">
          <Typography variant="h5" mb="16px">
            Price
          </Typography>
          {(ride.fare.priceLines || []).map((priceLine, idx) => (
            <Box key={idx} display="flex" justifyContent="space-between" mb="8px">
              <Typography color="text.secondary" fontWeight={400}>
                {priceLine.item}
              </Typography>
              <Typography
                color={priceLine.amount < 0 ? 'success.main' : 'text.secondary'}
                fontWeight={priceLine.amount < 0 ? 700 : 400}
              >
                {priceLine.displayAmount}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ marginTop: '16px' }} />
          <Box display="flex" justifyContent="space-between" mt="16px">
            <Typography fontWeight={700}>Total (USD)</Typography>
            <Typography fontWeight={700}>{ride.fare ? f.number.currency(ride.fare.finalAmount) : '-'}</Typography>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

const RideItem = ({ ride, trip, fullTripData, onRideLocationUpdated, isExcluded }) => {
  const rideId = ride.id || ride.rideId;
  const f = useFormatters();
  const c = useCopyToClipboard();
  const t = useToast();
  const dispatch = useDispatch();
  const [waiting, setWaiting] = useState(false);
  const [makeItRightOpen, setMakeItRightOpen] = useState(false);
  const hasMakeItRightPermission = useHasPermission('make_it_right');
  const hasEditRideLocationPermission = useHasPermission('edit_ride_location');
  const hasTransferRidePermission = useHasPermission('transfer_ride');

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [editLocationKind, setEditLocationKind] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);

  // Find the ride data from fullTripData using _id
  //const fullRide = fullTripData?.rides?.find(fullRide => fullRide._id === ride.id || fullRide._id === ride.rideId);
  const fullCancelledRide = fullTripData?.cancelledRides?.find(fullRide => fullRide._id === ride.id);

  const copyIdToClipboard = () => {
    c.copy(rideId);
    t.success('Ride id copied to clipboard');
  };

  const handleMakeItRight = () => {
    setMakeItRightOpen(true);
  };

  const handleMakeItRightClose = () => {
    setMakeItRightOpen(false);
  };

  const cancelRide = async () => {
    try {
      setWaiting(true);
      await apiCancelRide(rideId)(dispatch);
      setWaiting(false);
      setAnchorEl(null);
    } catch (err) {
      t.error('There was an error canceling this ride');
    }
  };

  const reinstateRide = async () => {
    try {
      if (fullCancelledRide?.cancellationReason === 'auto_cancel') {
        t.error("Auto cancelled rides can't be reinstated in Admin");
        setAnchorEl(null);
        return;
      }
      setWaiting(true);
      const res = await reinstateTripRide(trip.id, rideId)(dispatch);
      setWaiting(false);
      setAnchorEl(null);
      if (!res?.success) {
        t.error(res?.error || 'There was an error re-instating this ride');
        return;
      }
      // 200 means reinstatement was accepted. Round-trip re-pairing / placeholder
      // reconstruction happens asynchronously in dispatch, so this is "submitted",
      // not "paired".
      t.success('Reinstatement submitted. Re-pairing the round trip happens in the background.');
    } catch (err) {
      setWaiting(false);
      setAnchorEl(null);
      t.error('There was an error re-instating this ride');
    }
  };

  const pickUpRider = async () => {
    try {
      setWaiting(true);
      await pickUpRide(trip.id, rideId)(dispatch);
      setWaiting(false);
      setAnchorEl(null);
    } catch (err) {
      t.error('There was an error marking this ride as picked-up');
    }
  };

  const dropOffRider = async () => {
    try {
      setWaiting(true);
      await dropOffRide(trip.id, rideId)(dispatch);
      setWaiting(false);
      setAnchorEl(null);
    } catch (err) {
      t.error('There was an error marking this ride as dropped-off');
    }
  };

  const actions = [];

  if ([RideStatus.processing, RideStatus.finalizing_details, RideStatus.accepted].includes(ride.status)) {
    actions.push(
      <MenuItem key="pick-up" disabled={waiting} onClick={pickUpRider}>
        {waiting && <CircularProgress size={16} />}
        <Typography color="info.main" ml="5px">
          Mark as picked-up
        </Typography>
      </MenuItem>,
    );
  }

  if (ride.status === RideStatus.en_route) {
    actions.push(
      <MenuItem key="drop-off" disabled={waiting} onClick={dropOffRider}>
        {waiting && <CircularProgress size={16} />}
        <Typography color="info.main" ml="5px">
          Mark as dropped-off
        </Typography>
      </MenuItem>,
    );
  }

  if (ride.status === RideStatus.cancelled) {
    actions.push(
      <MenuItem key="reinstate" disabled={waiting} onClick={reinstateRide}>
        {!waiting && <ArrowForwardIcon color="info" sx={{ fontSize: 16 }} />}
        {waiting && <CircularProgress size={16} />}
        <Typography color="info.main" ml="5px">
          Reinstate rider
        </Typography>
      </MenuItem>,
    );
  }

  if (
    hasEditRideLocationPermission &&
    [RideStatus.processing, RideStatus.finalizing_details, RideStatus.accepted].includes(ride.status)
  ) {
    actions.push(
      <MenuItem key="edit-pickup" onClick={() => { setEditLocationKind('pickup'); setAnchorEl(null); }}>
        <Typography color="info.main" ml="5px">Edit pick-up location</Typography>
      </MenuItem>,
      <MenuItem key="edit-dropoff" onClick={() => { setEditLocationKind('dropoff'); setAnchorEl(null); }}>
        <Typography color="info.main" ml="5px">Edit drop-off location</Typography>
      </MenuItem>,
    );
  }

  if (
    hasTransferRidePermission &&
    [RideStatus.processing, RideStatus.finalizing_details, RideStatus.accepted].includes(ride.status)
  ) {
    actions.push(
      <MenuItem key="transfer" onClick={() => { setTransferOpen(true); setAnchorEl(null); }}>
        <ArrowForwardIcon color="info" sx={{ fontSize: 16 }} />
        <Typography color="info.main" ml="5px">Transfer to another rider</Typography>
      </MenuItem>,
    );
  }

  // Cancel is always the last action in the dropdown.
  if (![RideStatus.completed, RideStatus.cancelled].includes(ride.status)) {
    actions.push(
      <MenuItem key="cancel" color="error" disabled={waiting} onClick={cancelRide}>
        {!waiting && <CloseIcon color="error" />}
        {waiting && <CircularProgress size={16} />}
        <Typography color="error" ml="5px">
          Cancel rider
        </Typography>
      </MenuItem>,
    );
  }

  let creditsIssuedBlurb = 'Not Eligible';
  if (fullCancelledRide?.isRefundEligible) {
    if (fullCancelledRide?.didRefundCredits) {
      creditsIssuedBlurb = `${f.number.currency(
        (fullCancelledRide.cancellationCreditRefundAmount || 0) + 
        (fullCancelledRide.cancellationReferralCreditRefundAmount || 0) +
        (fullCancelledRide.creditRefundAmount || 0) +
        (fullCancelledRide.referralCreditRefundAmount || 0)
      )} - issued by system`;
    } else {
      creditsIssuedBlurb = 'N/A'
    }
  }

  let cancelledByBlurb = 'Rider';
  if (fullCancelledRide?.cancellationReason === 'rider_no_show') {
    cancelledByBlurb = 'Driver marked as no-show';
  } else if (fullCancelledRide?.latestPaymentTransaction?.status === 'FAILED') {
    cancelledByBlurb = 'System - payment failed when re-processing';
  } else if (fullCancelledRide?.cancellationReason) {
    cancelledByBlurb = fullCancelledRide?.cancellationReason;
  }

  return (
    // The dialog already pads its content; ListItem's default 16px gutter doubles it
    // and costs a narrow screen most of a word per line.
    <ListItem sx={{ px: { xs: 0, md: '16px' } }}>
      <Box display="flex" flexDirection="column" flex={1} minWidth={0} py="4px">
        {/* `justifyContent="space-between"` is what pushes the action menu to the
            right edge — the wide `spacing` was only reinforcing it, and at 100px it
            overflowed a phone-width dialog on its own. */}
        <Stack
          direction="row"
          spacing={{ xs: '4px', md: '100px' }}
          alignItems="center"
          justifyContent="space-between"
          py="8px"
          px="8px"
        >
          <Stack direction="row" alignItems="center" spacing={{ xs: '4px', md: '16px' }} flexWrap="wrap" rowGap="4px">
            <Button href={`/users/${ride.rider.id}`} target="_blank">
              <UserCard user={ride.rider} />
            </Button>
            {isExcluded && (
              <Tooltip title="Excluded pairing with this trip's driver — see Rider-Driver Exclusions." arrow>
                <span style={{ display: 'inline-flex' }}>
                  <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                </span>
              </Tooltip>
            )}
            <RideStatusChip status={ride.status} />
            <UserCrispLink userId={ride.rider.id} phoneNumber={ride.rider.phoneNumber} />
          </Stack>
          {actions.length > 0 && (
            <>
              <IconButton
                id={`${rideId}-menu-button`}
                aria-controls={menuOpen ? `${rideId}-menu` : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id={`${rideId}-menu`}
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{ 'aria-labelledby': `${rideId}-menu-button` }}
              >
                {actions}
              </Menu>
            </>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={{ xs: '8px', md: '48px' }} py="8px" px="8px">
          <Stack direction="row" spacing="8px" alignItems="center" flexWrap="wrap" rowGap="4px">
            <Typography color="text.secondary" variant="body2">
              Ride
            </Typography>
            <Typography>{rideId}</Typography>
            <IconButton size="small" onClick={copyIdToClipboard}>
              <ContentCopyIcon sx={{ fontSize: '16px' }} />
            </IconButton>
            <Button href={`/rides/${rideId}`} target="_blank" sx={{ textTransform: 'none' }}>
              View ride
            </Button>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: '8px', md: '48px' }}
          flexWrap="wrap"
          rowGap="4px"
          py="8px"
          px="8px"
        >
          {ride.departureTime && <DepartureTimes ride={ride} trip={trip} />}
          {ride.fare && <RideFare ride={ride} />}
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2">
              Contact
            </Typography>
            <Typography>{f.phone.toInternational(ride.rider.phoneNumber)}</Typography>
          </Stack>
        </Stack>
        
        {/* Pick-up and Drop-off information */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing="8px" py="8px" px="8px">
          <Box display="flex" maxWidth={{ xs: '100%', md: '50%' }}>
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap', marginRight: '8px' }}>
              Pick-up ({ride.pickUp ? ride.pickUp.type : ''})
            </Typography>
            <Typography>{ride.pickUp ? ride.pickUp.name || ride.pickUp.address : ''}</Typography>
          </Box>
          {/* Only a connector when the two sit side by side. */}
          <Typography sx={{ display: { xs: 'none', md: 'block' } }}> ➔ </Typography>
          <Box display="flex">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap', marginRight: '8px' }}>
              Drop-off ({ride.dropOff ? ride.dropOff.type : ''})
            </Typography>
            <Typography>{ride.dropOff ? ride.dropOff.name || ride.dropOff.address : ''}</Typography>
          </Box>
        </Stack>
        
        {/* Additional information for cancelled rides */}
        {fullCancelledRide && (<>
          <Stack direction="row" spacing={{ xs: '8px', md: '48px' }} flexWrap="wrap" rowGap="4px" py="8px" px="8px">
            <Stack direction="row" spacing="4px">
              <Typography color="text.secondary" variant="body2">
                Cancelled by
              </Typography>
              <Typography>
                {cancelledByBlurb}
              </Typography>
            </Stack>            
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Cancellation credits
              </Typography>
              <Typography>
                {creditsIssuedBlurb}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={{ xs: '8px', md: '48px' }} flexWrap="wrap" rowGap="4px" py="8px" px="8px">

            <Stack direction="row" spacing="4px">
              <Typography color="text.secondary" variant="body2">
                Cancellation policy
              </Typography>
              <Typography>
                {fullCancelledRide?.cancellationPolicy
                  ? `${fullCancelledRide?.cancellationPolicy.nonRefundableWithinMinutes / 60} hours`
                  : 'Non-refundable (standby)'}
              </Typography>
            </Stack>
            {fullCancelledRide?.statusUpdates && fullCancelledRide.statusUpdates.length > 0 && (
              (() => {
                const cancelledUpdate = fullCancelledRide.statusUpdates.find(update => update.status === 'cancelled');
                if (!cancelledUpdate) return null;
                
                // Get original departure time (first entry in history or current departure time)
                const originalDepartureTime = fullCancelledRide.departureTimeHistory && fullCancelledRide.departureTimeHistory.length > 0 
                  ? fullCancelledRide.departureTimeHistory[0].pastDeptTime 
                  : fullCancelledRide.departureTime;
                
                if (!originalDepartureTime) return null;
                
                // Calculate time difference
                const cancelledMoment = moment(cancelledUpdate.timestamp);
                const departureMoment = moment(originalDepartureTime);
                const diffMinutes = departureMoment.diff(cancelledMoment, 'minutes');
                const hours = Math.floor(Math.abs(diffMinutes) / 60);
                const minutes = Math.abs(diffMinutes) % 60;
                const timeDiffText = diffMinutes >= 0 
                  ? `${hours}h ${minutes}m before departure`
                  : `${hours}h ${minutes}m after departure`;
                
                return (
                  <Stack direction="row" spacing="8px">
                    <Typography color="text.secondary" variant="body2">
                      Cancelled
                    </Typography>
                    <Typography>
                      {timeDiffText}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      ({f.date.toTimezoneLocaleString(
                        cancelledUpdate.timestamp,
                        trip.originCity.timeZone,
                        'M/DD h:mm A z',
                      )})
                    </Typography>
                  </Stack>
                );
              })()
            )}
          </Stack>
        </>
        )}
        
        {/* Warning for unexpected completed status */}
        {fullCancelledRide?.status === RideStatus.completed && (
          <Stack direction="row" spacing="8px" py="8px" px="8px">
            <Typography color="error.main" variant="body2" sx={{ fontWeight: 'bold' }}>
              Unexpected completed status. Contact #tech-support
            </Typography>
          </Stack>
        )}
        
        <Stack direction="row" spacing="8px" py="8px" px="8px">
          {hasMakeItRightPermission && (
            <Button variant="outlined" onClick={handleMakeItRight}>
              Make it right
            </Button>
          )}
        </Stack>
      </Box>
      <MakeItRightDialog open={makeItRightOpen} onClose={handleMakeItRightClose} ride={ride} trip={trip} fullTripData={fullTripData} />
      {editLocationKind && (
        <EditRideLocationDialog
          open={Boolean(editLocationKind)}
          onClose={() => setEditLocationKind(null)}
          ride={ride}
          tripId={trip.id}
          kind={editLocationKind}
          onSuccess={onRideLocationUpdated}
        />
      )}
      {transferOpen && (
        <TransferRideDialog
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          ride={ride}
          onSuccess={onRideLocationUpdated}
        />
      )}
    </ListItem>
  );
};

const RidersList = ({ trip, rides, fullTripData, onRideLocationUpdated, excludedRiderIds }) => {
  return (
    <List>
      {rides.map((ride, idx) => {
        const rideId = ride.id || ride.rideId;
        return (
          <React.Fragment key={rideId}>
            <RideItem
              ride={ride}
              trip={trip}
              fullTripData={fullTripData}
              onRideLocationUpdated={onRideLocationUpdated}
              isExcluded={!!excludedRiderIds?.has(ride.rider.id)}
            />
            {idx < rides.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default RidersList;
